import { useCallback, useMemo } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H2, H3 } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconPlusSmall } from '@amzn/stencil-react-icons';

import { CriteriaEditorField } from './CriteriaEditorField';
import {
  TaskDetailsFields,
  type TaskDetailsValue,
  type TaskDetailsFieldsErrors,
} from '../../../shared/ui/forms/TaskDetailsFields';
import { useMessages } from '../../../shared/i18n/useMessages';
import type { CriteriaFieldErrors } from '../../../shared/domain/questionConfigValidation';
import {
  EMPTY_TASK_DETAILS,
  toTaskDetails,
  stripTaskDetails,
} from '../../../shared/domain/taskDetailsValidation';
import type { ReviewQuestion, QuestionContent } from '../logic/reviewTypes';

const BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type CriteriaKey =
  | 'criteriaStepHeading'
  | 'criteriaInfoAltText'
  | 'criteriaQuestionTextLabel'
  | 'criteriaQuestionTextPlaceholder'
  | 'criteriaQuestionTextInfo'
  | 'criteriaAuditInstructionLabel'
  | 'criteriaAuditInstructionPlaceholder'
  | 'criteriaAuditInstructionInfo'
  | 'criteriaPassLabel'
  | 'criteriaPassPlaceholder'
  | 'criteriaPassInfo'
  | 'criteriaFailLabel'
  | 'criteriaFailPlaceholder'
  | 'criteriaFailInfo'
  | 'criteriaJustificationLabel'
  | 'criteriaJustificationPlaceholder'
  | 'criteriaJustificationInfo'
  | 'criteriaAttachDragDropLabel'
  | 'criteriaTaskHeading'
  | 'criteriaTaskNameLabel'
  | 'criteriaTaskNamePlaceholder'
  | 'criteriaTaskDescriptionLabel'
  | 'criteriaTaskDescriptionPlaceholder'
  | 'criteriaTaskDurationLabel'
  | 'criteriaTaskDurationPlaceholder'
  | 'criteriaQuestionTextRequiredError'
  | 'criteriaAuditInstructionRequiredError'
  | 'criteriaPassCriteriaRequiredError'
  | 'criteriaFailCriteriaRequiredError'
  | 'criteriaJustificationRequiredError'
  // Task add/remove toggle + per-field required errors — the task strings are
  // task-generic and shared with the sub-question row (no criteria-specific
  // copies needed).
  | 'subAddTaskButton'
  | 'subRemoveTaskButton'
  | 'taskNameRequiredError'
  | 'taskDescriptionRequiredError'
  | 'taskDurationRequiredError';

/** The editable rich-text fields on `questionDetails`. */
export type CriteriaField =
  | 'questionText'
  | 'auditInstruction'
  | 'passCriteria'
  | 'failCriteria'
  | 'justification';

/** Per-field attachment files for one question's criteria fields. */
export type CriteriaAttachments = Record<CriteriaField, File[]>;

/** Reference-stable empty per-field attachments map. Exported so the owning
 *  page can seed state without re-allocating (and so identity checks hold). */
export const EMPTY_CRITERIA_ATTACHMENTS: CriteriaAttachments = {
  questionText: [],
  auditInstruction: [],
  passCriteria: [],
  failCriteria: [],
  justification: [],
};

export interface AuditCriteriaStepProps {
  /** The question under review whose criteria this step edits. */
  readonly question: ReviewQuestion;
  /** Emits a new question with the edited content. Parent owns draft state. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /**
   * Files attached per criteria field. Controlled by the owning page so they
   * survive step navigation (this component unmounts when the user moves to
   * another wizard step). PHASE 1: button-only — held in the page's UI state,
   * not uploaded or persisted onto the draft yet.
   */
  readonly attachments: CriteriaAttachments;
  /** Emits the full updated file list for one field on attach/remove. */
  readonly onAttachmentsChange: (field: CriteriaField, files: File[]) => void;
  /** Per-field inline-error flags (from `criteriaFieldErrors`). Each field
   *  shows the native invalid state + message when its flag is true; clears
   *  live as the field is filled. Defaults to all-false. */
  readonly fieldErrors?: CriteriaFieldErrors;
  /** Disables every field (e.g. once the question is published). */
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/** All-false field errors — module-level so an omitted `fieldErrors` prop is
 *  reference-stable and a clean step shows no inline errors. */
const NO_CRITERIA_FIELD_ERRORS: CriteriaFieldErrors = {
  questionText: false,
  auditInstruction: false,
  passCriteria: false,
  failCriteria: false,
  justification: false,
  taskName: false,
  taskDescription: false,
  taskDuration: false,
};

/** One rendered criteria row: labels/placeholder/info, current value, and an
 *  optional localized required-error message (present only when flagged). */
interface CriteriaFieldRow {
  readonly key: CriteriaField;
  readonly label: string;
  readonly placeholder: string;
  readonly info: string;
  readonly value: string;
  readonly error?: string;
}

/**
 * Builds the five criteria rows (question text, audit instruction, pass/fail
 * criteria, justification) with their localized labels, current values, and —
 * only when `fieldErrors` flags them — the required-error message. Pure and
 * module-level so the component body stays flat (no per-field error ternaries).
 */
function buildCriteriaFields(
  questionDetails: QuestionContent['questionDetails'],
  fieldErrors: CriteriaFieldErrors,
  t: (key: CriteriaKey) => string,
): readonly CriteriaFieldRow[] {
  return [
    {
      key: 'questionText',
      label: t('criteriaQuestionTextLabel'),
      placeholder: t('criteriaQuestionTextPlaceholder'),
      info: t('criteriaQuestionTextInfo'),
      value: questionDetails.questionText,
      ...(fieldErrors.questionText
        ? { error: t('criteriaQuestionTextRequiredError') }
        : {}),
    },
    {
      key: 'auditInstruction',
      label: t('criteriaAuditInstructionLabel'),
      placeholder: t('criteriaAuditInstructionPlaceholder'),
      info: t('criteriaAuditInstructionInfo'),
      value: questionDetails.auditInstruction,
      ...(fieldErrors.auditInstruction
        ? { error: t('criteriaAuditInstructionRequiredError') }
        : {}),
    },
    {
      key: 'passCriteria',
      label: t('criteriaPassLabel'),
      placeholder: t('criteriaPassPlaceholder'),
      info: t('criteriaPassInfo'),
      value: questionDetails.passCriteria,
      ...(fieldErrors.passCriteria
        ? { error: t('criteriaPassCriteriaRequiredError') }
        : {}),
    },
    {
      key: 'failCriteria',
      label: t('criteriaFailLabel'),
      placeholder: t('criteriaFailPlaceholder'),
      info: t('criteriaFailInfo'),
      value: questionDetails.failCriteria,
      ...(fieldErrors.failCriteria
        ? { error: t('criteriaFailCriteriaRequiredError') }
        : {}),
    },
    {
      key: 'justification',
      label: t('criteriaJustificationLabel'),
      placeholder: t('criteriaJustificationPlaceholder'),
      info: t('criteriaJustificationInfo'),
      value: questionDetails.justification,
      ...(fieldErrors.justification
        ? { error: t('criteriaJustificationRequiredError') }
        : {}),
    },
  ];
}

/**
 * First review wizard step: the editable "Audit criteria" form. Each criteria
 * field (question text, audit instructions, pass/fail criteria, justification)
 * and the optional task description are rich-text editors, reusing the shared
 * `RichTextEditorField` (Stencil/TipTap) so the review flow matches the manual
 * question-creation flow without a second editor implementation. Task name and
 * duration are plain inputs.
 *
 * Fully controlled — it derives a new `ReviewQuestion` on every edit and hands
 * it to `onQuestionChange`; the owning page/hook holds the draft state.
 */
export function AuditCriteriaStep({
  question,
  onQuestionChange,
  attachments,
  onAttachmentsChange,
  fieldErrors = NO_CRITERIA_FIELD_ERRORS,
  disabled = false,
  dataTestId,
}: AuditCriteriaStepProps): JSX.Element {
  const { t, isLoading: isBundleLoading } = useMessages<CriteriaKey>(BUNDLE);
  const { questionDetails, taskDetails } = question.content;

  // Patch a single questionDetails field, preserving the rest of the draft.
  const handleCriteriaChange = useCallback(
    (field: CriteriaField, value: string) => {
      onQuestionChange({
        ...question,
        content: {
          ...question.content,
          questionDetails: { ...question.content.questionDetails, [field]: value },
        },
      });
    },
    [question, onQuestionChange],
  );

  // Write the edited task back onto the draft's content. TaskDetailsFields
  // owns the per-field editing; this re-parents the value (normalized via the
  // shared toTaskDetails) into the question and emits the new draft.
  const handleTaskChange = useCallback(
    (next: TaskDetailsValue) => {
      const nextContent: QuestionContent = {
        ...question.content,
        taskDetails: toTaskDetails(next),
      };
      onQuestionChange({ ...question, content: nextContent });
    },
    [question, onQuestionChange],
  );

  // Add Task: attach an empty task shell (mirrors the sub-question row). Its
  // three fields become required and validate from that point.
  const handleAddTask = useCallback(() => {
    const nextContent: QuestionContent = {
      ...question.content,
      taskDetails: EMPTY_TASK_DETAILS,
    };
    onQuestionChange({ ...question, content: nextContent });
  }, [question, onQuestionChange]);

  // Remove Task: drop the taskDetails key entirely (exactOptionalPropertyTypes
  // -clean) so the criteria is publishable again — the task is optional.
  const handleRemoveTask = useCallback(() => {
    onQuestionChange({
      ...question,
      content: stripTaskDetails(question.content),
    });
  }, [question, onQuestionChange]);

  const hasTask = taskDetails !== undefined;
  const taskValue: TaskDetailsValue = taskDetails ?? EMPTY_TASK_DETAILS;

  // Map the criteria's task field-error flags into the localized MESSAGES
  // TaskDetailsFields renders (undefined = no error). Memoized so the child
  // prop stays reference-stable across renders — identical pattern to
  // SubQuestionRow.
  const taskErrors = useMemo<TaskDetailsFieldsErrors>(
    () => ({
      ...(fieldErrors.taskName ? { name: t('taskNameRequiredError') } : {}),
      ...(fieldErrors.taskDescription
        ? { description: t('taskDescriptionRequiredError') }
        : {}),
      ...(fieldErrors.taskDuration ? { duration: t('taskDurationRequiredError') } : {}),
    }),
    [fieldErrors, t],
  );

  const criteriaFields = buildCriteriaFields(questionDetails, fieldErrors, t);

  const infoAltText = t('criteriaInfoAltText');

  return (
    // Flat content (no section cards): the review page wraps the whole step in
    // a single outer card, so Audit criteria and Task details render as one
    // continuous flow separated by spacing only (no divider lines). Section
    // headings are H2 (the page title is the H1).
    <Col
      gridGap="dimensions.spacing.400"
      width="100%"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.500" width="100%">
        <H2>{t('criteriaStepHeading')}</H2>

        {/* The editor's toolbar config needs resolved i18n labels, so hold
         *  rendering until the bundle loads (mirrors TaskFormFields). The
         *  fields are separated by the Col's gridGap spacing only — no divider
         *  line between them (UX: space, not rules, between criteria fields). */}
        {isBundleLoading
          ? null
          : criteriaFields.map((field) => (
              <CriteriaEditorField
                key={field.key}
                field={field.key}
                label={field.label}
                placeholder={field.placeholder}
                info={field.info}
                infoAltText={infoAltText}
                value={field.value}
                disabled={disabled}
                onChange={handleCriteriaChange}
                files={attachments[field.key]}
                onFilesChange={onAttachmentsChange}
                dragAndDropText={t('criteriaAttachDragDropLabel')}
                {...(field.error !== undefined ? { error: field.error } : {})}
                {...(dataTestId !== undefined
                  ? { dataTestId: `${dataTestId}-${field.key}` }
                  : {})}
              />
            ))}
      </Col>

      {/* The criteria fields and the Task Details section are separated by the
       *  outer Col's gridGap spacing only — no divider line between them. */}
      <Col
        gridGap="dimensions.spacing.300"
        width="100%"
        {...(dataTestId !== undefined
          ? { dataTestId: `${dataTestId}-task-section` }
          : {})}
      >
        {/* Task hidden behind an Add Task toggle, mirroring the sub-question
         *  row: no task by default, and once added all three fields are
         *  required and validate. The "Task Details" heading only appears once
         *  a task is attached — before then just the "+ Add Task" button shows.
         */}
        <Row
          alignItems="center"
          justifyContent={hasTask ? 'space-between' : 'flex-end'}
          flexWrap="wrap"
        >
          {hasTask ? <H3>{t('criteriaTaskHeading')}</H3> : null}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={hasTask ? handleRemoveTask : handleAddTask}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-task-toggle` : undefined}
            {...(hasTask ? {} : { icon: <IconPlusSmall aria-hidden="true" /> })}
          >
            {hasTask ? t('subRemoveTaskButton') : t('subAddTaskButton')}
          </Button>
        </Row>

        {/* TaskDetailsFields renders a RichTextEditorField whose toolbar needs
         *  resolved i18n labels, so hold until the bundle loads. Shown only
         *  when a task is attached; all fields required (isRequired) and wired
         *  to the live per-field errors. */}
        {hasTask && !isBundleLoading && (
          <TaskDetailsFields
            value={taskValue}
            onChange={handleTaskChange}
            disabled={disabled}
            errors={taskErrors}
            trackingIdPrefix="audit-whiz.review.criteria"
            labels={{
              nameLabel: t('criteriaTaskNameLabel'),
              namePlaceholder: t('criteriaTaskNamePlaceholder'),
              descriptionLabel: t('criteriaTaskDescriptionLabel'),
              descriptionPlaceholder: t('criteriaTaskDescriptionPlaceholder'),
              durationLabel: t('criteriaTaskDurationLabel'),
              durationPlaceholder: t('criteriaTaskDurationPlaceholder'),
            }}
            {...(dataTestId !== undefined ? { dataTestId } : {})}
          />
        )}
      </Col>
    </Col>
  );
}
